//React Hooks
import { useState, useRef, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

//MUI Components
import {
  Box,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Divider,
  TextField,
  IconButton,
  Grid2,
  Stack,
  Checkbox,
} from "@mui/material";

//MUI Icons
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DoneIcon from "@mui/icons-material/Done";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import SendIcon from "@mui/icons-material/Send";

//Custom components
import CommonAccordion from "../../custom-components/CommonAccordion";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";
import { SecurityFlowDataContext, SecurityLoaderStateContext } from "../../main-pages/SecurityTestingPage";
import { ReactComponent as DeleteIcon } from "../../../images/SecurityDeleteIcon.svg";
import { brdToArchDesign, brdToFuncModule, funcModToSecurityReq, finalbrd, finalbrdFromUserStory } from "../../../services/webServices";
import SessionExpired from "../../popup-components/SessionExpiredPopup";

//Document related libraries
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import JSZip from "jszip";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;


//Before Upload
const FileInputBox = ({ onClick }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      p: 2,
      backgroundColor: "#EAF4FF",
      borderRadius: "8px",
      border: "1px dashed #49A3FF",
      cursor: "pointer",
      mt: 1,
    }}
    onClick={onClick}
  >
    <Box>
      <ContentPasteSearchIcon sx={{ mr: 1 }} />
    </Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography sx={{ color: "#000" }}>Upload Documents</Typography>
      <Typography variant="caption" sx={{ color: "gray" }}>
        Pdf, Doc, Text, Excel
      </Typography>
    </Box>
  </Box>
);


// After upload
const FileUploadPreview = ({ file, onChangeClick, onRemove }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: 2,
      mt: 1,
      borderRadius: "8px",
      border: "1px dashed #49A3FF",
      backgroundColor: "#EAF4FF",
    }}
  >
    <Typography
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <ContentPasteSearchIcon sx={{ mr: 1 }} />
      {file.name}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Button
        variant="text"
        size="small"
        sx={{ textTransform: "none", fontSize: "13px", minWidth: "60px" }}
        onClick={onChangeClick}
      >
        Change
      </Button>
      <IconButton color="error" onClick={onRemove} size="small">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  </Box>
);


// BRD Component
const BRDComponent = ({
  fileRefMap,
  uploadedBRDFile,
  handleFileUpload,
  handleRemove,
}) => {
  return (
    <Paper
      sx={{ mt: 2, p: 3, borderRadius: "12px", backgroundColor: "#F8FBFF" }}
    >
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Business Requirement Document (BRD)
      </Typography>

      {uploadedBRDFile ? (
        <FileUploadPreview
          file={uploadedBRDFile}
          onChangeClick={() => fileRefMap.brd.current.click()}
          onRemove={() => handleRemove("brd")}
        />
      ) : (
        <FileInputBox onClick={() => fileRefMap.brd.current.click()} />
      )}

      <input
        type="file"
        accept=".xlsx,.xls,.docx,.txt,.pdf"
        style={{ display: "none" }}
        ref={fileRefMap.brd}
        onChange={(e) => handleFileUpload(e, "brd")}
      />
    </Paper>
  );
};


//Userstory component
const UserStoriesComponent = ({
  fileRef,
  uploadedFile,
  handleFileUpload,
  handleRemove,
  stories,
  setStories,
}) => {
  const [isAllSelected, setIsAllSelected] = useState(false);
  const handleAddStory = () => {
    setStories([...stories, { text: "", checked: false, isEditing: true }]);
  };
  const handleStoryChange = (index, newText) => {
    setStories((prev) =>
      prev.map((story, i) =>
        i === index ? { ...story, text: newText, hasChanges: true } : story
      )
    );
  };
  const handleCheckChange = (index) => {
    const updated = [...stories];
    updated[index].checked = !updated[index].checked;
    setStories(updated);
  };
  const handleDeleteStory = (index) => {
    const updated = stories.filter((_, i) => i !== index);
    setStories(updated);
  };
  const handleEditStory = (index) => {
    if (!Array.isArray(stories)) {
      console.warn("stories is not an array:", stories);
      return;
    }
    const updated = stories.map((s, i) =>
      i === index
        ? { ...s, isEditing: !s.isEditing }
        : s
    );
    setStories(updated);
  };
  const handleSelectAll = () => {
    if (!Array.isArray(stories)) {
      console.warn("stories is not an array:", stories);
      return;
    }
    setStories(stories.map((s) => ({ ...s, checked: true })));
  };
  const handleDeselectAll = () => {
    if (!Array.isArray(stories)) {
      console.warn("stories is not an array:", stories);
      return;
    }
    setStories(stories.map((s) => ({ ...s, checked: false })));
  };
  return (
    <Paper
      sx={{ mt: 2, p: 3, borderRadius: "12px", backgroundColor: "#F8FBFF" }}
    >
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        User Stories
      </Typography>
      {uploadedFile ? (
        <FileUploadPreview
          file={uploadedFile}
          onChangeClick={() => fileRef.current.click()}
          onRemove={handleRemove}
        />
      ) : (
        <FileInputBox onClick={() => fileRef.current.click()} />
      )}
      <input
        type="file"
        accept=".xlsx,.xls,.docx,.txt,.pdf"
        style={{ display: "none" }}
        ref={fileRef}
        onChange={handleFileUpload}
      />
      <Divider sx={{ my: 3 }}>OR</Divider>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, mr: 1.5 }}>
        <Button
          onClick={() => {
            if (isAllSelected) {
              handleDeselectAll();
            } else {
              handleSelectAll();
            }
            setIsAllSelected((prev) => !prev);
          }}
          sx={{ textTransform: 'none' }}
        >
          {isAllSelected ? 'Unselect all' : 'Select all'}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddStory}
          sx={{ color: 'primary.main', textTransform: 'none' }}
        >
          Add
        </Button>
      </Stack>
      {Array.isArray(stories) &&
        stories.map((story, index) => (
          <Box
            key={index}
            sx={{
              mt: 1,
              p: 2,
              borderRadius: 2,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="start"
              sx={{ ml: -2 }}
            >
              <Checkbox
                checked={story.checked}
                onChange={() => handleCheckChange(index)}
                sx={{ mt: 1, color: "grey" }}
              />
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter user story"
                label="Enter User Story"
                multiline
                rows={3}
                value={story.text}
                onChange={(e) => handleStoryChange(index, e.target.value)}
                disabled={!story.isEditing}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1, mr: -1 }}>
              <IconButton onClick={() => handleEditStory(index)}>
                {story.isEditing ? (
                  <DoneIcon
                    sx={{ color: story.hasChanges ? 'primary.main' : 'grey' }}
                  />
                ) : (
                  <EditOutlinedIcon sx={{ color: 'primary.main' }} />
                )}
              </IconButton>
              {stories.length > 1 && (
                <IconButton onClick={() => handleDeleteStory(index)}>
                  <DeleteIcon sx={{ color: '#d32f2f' }} />
                </IconButton>
              )}
            </Stack>
          </Box>
        ))}
    </Paper>
  );
};


// Workflow Component
const WorkflowComponent = ({
  fileRef,
  uploadedFile,
  uploadedADD,
  handleFileUpload,
  handleRemove,
  workflowInput,
  setWorkflowInput,
}) => {
  return (
    <Paper
      sx={{ mt: 2, p: 3, borderRadius: "12px", backgroundColor: "#F8FBFF" }}
    >
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Workflow Documentation
      </Typography>

      {uploadedFile ? (
        <FileUploadPreview
          file={uploadedFile}
          onChangeClick={() => fileRef.main.current.click()}
          onRemove={() => handleRemove("Workflow")}
        />
      ) : (
        <FileInputBox onClick={() => fileRef.main.current.click()} />
      )}

      <input
        type="file"
        accept=".xlsx,.xls,.docx,.txt,.pdf"
        style={{ display: "none" }}
        ref={fileRef.main}
        onChange={(e) => handleFileUpload(e, "Workflow")}
      />

      <Divider sx={{ my: 3 }}>OR</Divider>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Enter workflow description"
        label="Workflow Documentation"
        multiline
        minRows={3}
        sx={{ mt: 1, mb: 2 }}
        value={workflowInput}
        onChange={(e) => setWorkflowInput(e.target.value)}
      />
    </Paper>
  );
};


// ADD Component
const ADDComponent = ({ fileRef, uploadedFile, handleFileUpload, handleRemove }) => {
  return (
    <Paper sx={{ mt: 2, p: 3, borderRadius: "12px", backgroundColor: "#F8FBFF" }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Architecture Design Document (ADD)
      </Typography>

      {uploadedFile ? (
        <FileUploadPreview
          file={uploadedFile}
          onChangeClick={() => fileRef.current.click()}
          onRemove={handleRemove}
        />
      ) : (
        <FileInputBox onClick={() => fileRef.current.click()} />
      )}

      <input
        type="file"
        accept=".xlsx,.xls,.docx,.txt,.pdf"
        style={{ display: "none" }}
        ref={fileRef}
        onChange={handleFileUpload}
      />
    </Paper>
  );
};



const RequirementsPhase = () => {

  //UseContext and navigation
  const { setLoadingState } = useContext(LoadingModalContext);
  const { securityLoaderState, setSecurityLoaderState } = useContext(SecurityLoaderStateContext);
  const {
    setFuncMod,
    setSecurityReq,
    setArchDesign,
    setBRD,
    setUserStory,
    setWorkFlow,
    setSecRecommendations,
    setThreatModelling,
    setAddReview,
    setSecurityTestPlan,
  } = useContext(SecurityFlowDataContext);


  const navigate = useNavigate();

  //State variables for input
  const [brdInput, setBrdInput] = useState("");
  const [workflowInput, setWorkflowInput] = useState("");
  const [addInput, setAddInput] = useState("");
  const [uploadedUserStories, setUploadedUserStories] = useState([]);
  const [openUploadUserStory, setOpenUploadUserStory] = useState(false);
  const [stories, setStories] = useState([]);
  const [inputType, setInputType] = useState("BRD");

  // Split uploaded file states to avoid creating one object reference
  const [uploadedBRDFile, setUploadedBRDFile] = useState(null);
  const [uploadedADDFile, setUploadedADDFile] = useState(null);
  const [uploadedUserStoriesFile, setUploadedUserStoriesFile] = useState(null);
  const [uploadedWorkflowFile, setUploadedWorkflowFile] = useState(null);

  //State variables for output
  const [funcmodInput, setFuncmodInput] = useState(null);
  const [securityReqInput, setSecurityReqInput] = useState(null);
  const [archInput, setArchInput] = useState(null);

  //State variable for error popup 
  const [openErrorPopup, setOpenErrorPopup] = useState(false);


  //  Load uploaded file info from localStorage when page reloads
  useEffect(() => {
    const storedBRD = localStorage.getItem("uploadedBRDFile");
    if (storedBRD) setUploadedBRDFile(JSON.parse(storedBRD));

    const storedADD = localStorage.getItem("uploadedADDFile");
    if (storedADD) setUploadedADDFile(JSON.parse(storedADD));

    const storedUserStories = localStorage.getItem("uploadedUserStoriesFile");
    if (storedUserStories) setUploadedUserStoriesFile(JSON.parse(storedUserStories));

    const storedWorkflow = localStorage.getItem("uploadedWorkflowFile");
    if (storedWorkflow) setUploadedWorkflowFile(JSON.parse(storedWorkflow));
  }, []);


  //File reference
  const fileRefMap = {
    brd: useRef(null),
    UserStories: useRef(null),
    Workflow: {
      main: useRef(null),
      add: useRef(null),
    },
    add: useRef(null),
  };


  //Default Editable User Story field 
  useEffect(() => {
    if (inputType === "UserStories" && stories.length === 0) {
      setStories([
        { text: "", checked: false, isEditing: true, hasChanges: false },
      ]);
    }
  }, [inputType]);


  //Input handling 
  const handleInputTypeChange = (e) => {
    const newType = e.target.value;
    if (newType === inputType) {
      console.log("handleInputTypeChange skipped");
      return;
    }
    console.log("Changing input type to", newType);
    setInputType(newType);

    if (newType !== "UserStories") setStories([]);
    if (newType !== "Workflow") setWorkflowInput("");
    if (newType !== "BRD") setBrdInput("");
    if (newType !== "ADD") setAddInput("");

    setUploadedUserStoriesFile(null);
    setUploadedWorkflowFile(null);
    setUploadedBRDFile(null);
    setUploadedADDFile(null);

    setUploadedUserStories([]);
  };


  //File Upload
  const handleFileUpload = async (event, type) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) return;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    processFileAsync(file, type);
    event.target.value = null;
  };


  // File Reading & Processing
  const processFileAsync = async (file, type) => {
    try {
      setUploadedUserStories([]);
      setOpenUploadUserStory(false);
      const fileName = file.name.toLowerCase();
      const allowedExtensions = [".xlsx", ".xls", ".docx", ".txt", ".pdf"];
      const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));
      if (!isAllowed) {
        alert("Unsupported file type. Please upload a .pdf, .docx, .xlsx, or .xls file.");
        setLoadingState({ open: false, message: "", progress: null });
        return;
      }
      const validateExtractedContent = (text, fileType) => {
        if (!text || text.trim().length === 0) {
          alert(`Uploaded ${fileType.toUpperCase()} file is empty`);
          setLoadingState({ open: false, message: "", progress: null });
          return false;
        }
        return true;
      };
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const result = e.target.result;
          let extracted = null;
          // Excel
          if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
            const data = new Uint8Array(result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const sheetJson = XLSX.utils.sheet_to_json(sheet);
            if (!sheetJson || sheetJson.length === 0) {
              alert("Uploaded Excel file is empty");
              setLoadingState({ open: false, message: "", progress: null });
              return;
            }
            if (!validateExtractedContent(JSON.stringify(sheetJson), "Excel")) return;
            extracted = sheetJson;
            setUploadedUserStories(sheetJson);
          }
          // DOCX
          else if (fileName.endsWith(".docx")) {
            let { value } = await mammoth.extractRawText({ arrayBuffer: result });
            let extractedText = value.trim();
            if (extractedText.length < 10) {
              const zip = await JSZip.loadAsync(result);
              let ocrText = "";
              const imageFiles = Object.keys(zip.files).filter(name => name.startsWith("word/media/"));
              for (const imgName of imageFiles) {
                const imgData = await zip.files[imgName].async("base64");
                const imgUrl = `data:image/${imgName.split('.').pop()};base64,${imgData}`;
                const ocrResult = await Tesseract.recognize(imgUrl, "eng");
                ocrText += ocrResult.data.text + "\n";
              }
              extractedText = ocrText.trim();
            }
            if (!validateExtractedContent(extractedText, "DOCX")) return;
            extracted = extractedText;
            setUploadedUserStories([{ content: extractedText }]);
          }
          // TXT
          else if (fileName.endsWith(".txt")) {
            if (!validateExtractedContent(result, "TXT")) return;
            extracted = result;
            setUploadedUserStories([{ content: result }]);
          }
          // PDF
          else if (fileName.endsWith(".pdf")) {
            const typedArray = new Uint8Array(result);
            const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
            let pdfText = "";
            for (let i = 1; i <= pdf.numPages; i++) {
              const page = await pdf.getPage(i);
              const content = await page.getTextContent();
              const strings = content.items.map(item => item.str).join(" ").trim();
              if (strings.length > 10) {
                pdfText += strings + "\n";
              } else {
                const viewport = page.getViewport({ scale: 2.0 });
                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                await page.render({ canvasContext: context, viewport }).promise;
                const imageData = canvas.toDataURL("image/png");
                const ocrResult = await Tesseract.recognize(imageData, "eng");
                pdfText += ocrResult.data.text + "\n";
              }
            }
            pdfText = pdfText.trim();
            if (!validateExtractedContent(pdfText, "PDF")) return;
            extracted = pdfText;
            setUploadedUserStories([{ content: pdfText }]);
          }

          // show loader once valid content is confirmed
          setLoadingState({ open: true, message: "Processing your file...", progress: null });

          if (type === "brd") {
            setUploadedBRDFile(file);
            localStorage.setItem("uploadedBRDFile", JSON.stringify({
              name: file.name,
              type: file.type,
              lastModified: file.lastModified
            }));
          } else if (type === "add") {
            setUploadedADDFile(file);
            localStorage.setItem("uploadedADDFile", JSON.stringify({
              name: file.name,
              type: file.type,
              lastModified: file.lastModified
            }));
          } else if (type === "UserStories") {
            setUploadedUserStoriesFile(file);
            localStorage.setItem("uploadedUserStoriesFile", JSON.stringify({
              name: file.name,
              type: file.type,
              lastModified: file.lastModified
            }));
          } else if (type === "Workflow") {
            setUploadedWorkflowFile(file);
            localStorage.setItem("uploadedWorkflowFile", JSON.stringify({
              name: file.name,
              type: file.type,
              lastModified: file.lastModified
            }));
          }

          setOpenUploadUserStory(true);
        } catch (err) {
          console.error("File processing error:", err);
          alert("There was an error processing the file.");
          setLoadingState({ open: false, message: "", progress: null });
        } finally {
          setLoadingState({ open: false, message: "", progress: null });
        }
      };
      if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls") || fileName.endsWith(".docx") || fileName.endsWith(".pdf")) {
        reader.readAsArrayBuffer(file);
      } else if (fileName.endsWith(".txt")) {
        reader.readAsText(file);
      }
    } catch (err) {
      console.error("Unexpected processing error:", err);
      setLoadingState({ open: false, message: "", progress: null });
    }
  };


  //File Remove
  const handleRemove = (type) => {
    if (type === "add") {
      setUploadedADDFile(null);
      fileRefMap.Workflow.add.current.value = null;
      localStorage.removeItem("uploadedADDFile");

      // Clear Requirement Phase outputs
      setArchDesign(null);

      // Clear Design Phase outputs
      setSecRecommendations(null);
      setThreatModelling(null);
      setAddReview(null);
      setSecurityTestPlan(null);

      // Remove from localStorage
      localStorage.removeItem("archDesign");
      localStorage.removeItem("secRecommendations");
      localStorage.removeItem("threatModelling");
      localStorage.removeItem("addReview");
      localStorage.removeItem("securityTestPlan");
    } else if (type === "brd") {
      setUploadedBRDFile(null);
      fileRefMap.brd.current.value = null;
      localStorage.removeItem("uploadedBRDFile");

      // Clear Requirement Phase outputs
      setFuncMod(null);
      setSecurityReq(null);
      setArchDesign(null);
      setBRD(null);

      // Clear Design Phase outputs
      setSecRecommendations(null);
      setThreatModelling(null);
      setAddReview(null);
      setSecurityTestPlan(null);

      // Remove from localStorage
      localStorage.removeItem("funcMod");
      localStorage.removeItem("securityReq");
      localStorage.removeItem("archDesign");
      localStorage.removeItem("brd");

      localStorage.removeItem("secRecommendations");
      localStorage.removeItem("threatModelling");
      localStorage.removeItem("addReview");
      localStorage.removeItem("securityTestPlan");
    } else if (type === "UserStories") {
      setUploadedUserStoriesFile(null);
      fileRefMap.UserStories.current.value = null;
      localStorage.removeItem("uploadedUserStoriesFile");

      // Clear Requirement Phase outputs
      setFuncMod(null);
      setSecurityReq(null);
      setArchDesign(null);
      setUserStory(null);

      // Clear Design Phase outputs
      setSecRecommendations(null);
      setThreatModelling(null);
      setAddReview(null);
      setSecurityTestPlan(null);

      // Remove from localStorage
      localStorage.removeItem("funcMod");
      localStorage.removeItem("securityReq");
      localStorage.removeItem("archDesign");
      localStorage.removeItem("userStory");

      localStorage.removeItem("secRecommendations");
      localStorage.removeItem("threatModelling");
      localStorage.removeItem("addReview");
      localStorage.removeItem("securityTestPlan");
    } else if (type === "Workflow") {
      setUploadedWorkflowFile(null);
      fileRefMap.Workflow.main.current.value = null;
      localStorage.removeItem("uploadedWorkflowFile");

      // Clear Requirement Phase outputs
      setFuncMod(null);
      setSecurityReq(null);
      setArchDesign(null);
      setWorkFlow(null);

      // Clear Design Phase outputs
      setSecRecommendations(null);
      setThreatModelling(null);
      setAddReview(null);
      setSecurityTestPlan(null);

      // Remove from localStorage
      localStorage.removeItem("funcMod");
      localStorage.removeItem("securityReq");
      localStorage.removeItem("archDesign");
      localStorage.removeItem("workFlow");

      localStorage.removeItem("secRecommendations");
      localStorage.removeItem("threatModelling");
      localStorage.removeItem("addReview");
      localStorage.removeItem("securityTestPlan");
    }

    // Reset any other states
    setUploadedUserStories([]);
  };


  //Enable Button
  const isButtonEnabled = () => {
    if (inputType === "BRD")
      return !!uploadedBRDFile || brdInput.trim() !== "";
    if (inputType === "UserStories") {
      const hasCheckedStory =
        Array.isArray(stories) &&
        stories.some((story) => story.checked && story.text.trim() !== "");
      return !!uploadedUserStoriesFile || hasCheckedStory;
    }
    if (inputType === "Workflow")
      return !!uploadedWorkflowFile || workflowInput.trim() !== "";
    if (inputType === "ADD")
      return !!uploadedADDFile;
    return false;
  };


  //Handling function for Requirement Phase
  const handleIdentifyClick = async () => {
    let progressHandler;
    try {
      setSecurityLoaderState("Requirements Phase");
      let results = [];
      if (inputType === "UserStories") {
        if (uploadedUserStoriesFile) {
          // File upload flow - User Story
          let payload = {};
          if (uploadedUserStoriesFile.name?.match(/\.(xlsx|xls)$/)) {
            payload.user_story = JSON.stringify(uploadedUserStories);
          } else {
            payload.user_story = uploadedUserStories[0]?.content;
          }
          try {
            progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 90000 });
            progressHandler.start("Generating Functional Modules");
            const finalBRDResponse = await finalbrdFromUserStory(payload);
            const finalBRD =
              finalBRDResponse.data["Business Requirements"]["Functional Requirements"];

            const funcModResponse = await brdToFuncModule({ brd: finalBRD });
            const funcModules = funcModResponse.data;
            setFuncmodInput(funcModules);

            progressHandler.updateMessage("Generating Security Requirements");

            const securityResponse = await funcModToSecurityReq(funcModules);
            const securityData = securityResponse.data;
            setSecurityReqInput(securityData);

            progressHandler.updateMessage("Generating System Architecture");

            const archResponse = await brdToArchDesign({ brd: finalBRD });
            const archData = archResponse.data;
            setArchInput(archData);


            await progressHandler.finishAndClose();
            results.push({
              userStory: payload.user_story,
              funcMod: funcModules,
              securityReq: securityData,
              archDesign: archData,
              brd: finalBRD
            });

            navigate("/dashboard/security-testing/requirement-output", {
              state: results[0],
            });
          } catch (error) {
            setLoadingState({ open: false, message: "", progress: null });
            throw error;
          }
        } else {
          // Checkbox selection flow - User Story
          const selectedStories = stories.filter(
            (story) => story.checked && story.text.trim() !== ""
          );
          if (selectedStories.length === 0) {
            throw new Error("No user stories selected.");
          }
          progressHandler = handleProgressBar({
            setLoadingState,
            totalDurationMs: 90000,
          });
          progressHandler.start("Generating Functional Modules");

          const storyPayloads = selectedStories.map((story) =>
            story.text.trim()
          );
          const finalBRDResponse = await finalbrd(storyPayloads);
          const finalBRD = finalBRDResponse.data;
          progressHandler.updateMessage("Generating Functional Modules");

          const funcModResponse = await brdToFuncModule({
            brd: finalBRD,
          });
          const funcModules = funcModResponse.data;
          setFuncmodInput(funcModules);


          progressHandler.updateMessage("Generating Security Requirements");
          const securityResponse = await funcModToSecurityReq(funcModules);
          const securityReq = securityResponse.data;
          setSecurityReqInput(securityReq);

          progressHandler.updateMessage("Generating System Architecture");
          const archResponse = await brdToArchDesign({
            brd: finalBRD,
          });
          const archData = archResponse.data;
          setArchInput(archData);

          await progressHandler.finishAndClose();
          console.log({
            funcMod: funcModules,
            securityReq,
            archDesign: archData,
            brd: finalBRD,
          });
          navigate("/dashboard/security-testing/requirement-output", {
            state: {
              funcMod: funcModules,
              securityReq,
              archDesign: archData,
              brd: finalBRD,
            },
          });
        }
      } else {
        // BRD, ADD or Workflow flow
        progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 90000 });
        progressHandler.start("Generating Functional Modules");
        const payload = {};
        if (inputType === "BRD") {
          if (uploadedBRDFile) {
            if (uploadedBRDFile.name?.match(/\.(xlsx|xls)$/)) {
              payload.brd = JSON.stringify(uploadedUserStories);
            } else {
              payload.brd = uploadedUserStories[0]?.content;
            }
          } else if (brdInput?.trim()) {
            payload.brd = brdInput;
          }
        } else if (inputType === "Workflow") {
          if (uploadedWorkflowFile) {
            if (uploadedWorkflowFile.name?.match(/\.(xlsx|xls)$/)) {
              payload.workflow = JSON.stringify(uploadedUserStories);
            } else {
              payload.workflow = uploadedUserStories[0]?.content;
            }
          } else if (workflowInput?.trim()) {
            payload.workflow = workflowInput;
          }
        } else if (inputType === "ADD") {
          if (uploadedADDFile) {
            if (uploadedADDFile.name?.match(/\.(xlsx|xls)$/)) {
              payload.add = uploadedUserStories;
            } else {
              payload.add = uploadedUserStories[0]?.content || "";
            }
          }
        }
        if (Object.keys(payload).length === 0) {
          throw new Error(
            "Please provide input either by uploading a file or entering text."
          );
        }
        try {
          const finalBRDResponse = await finalbrd(payload);
          const finalBRD = finalBRDResponse.data;

          const funcModResponse = await brdToFuncModule({ brd: finalBRD });
          const funcModules = funcModResponse.data;
          setFuncmodInput(funcModules);

          progressHandler.updateMessage("Generating Security Requirements");

          const securityResponse = await funcModToSecurityReq(funcModules);
          const securityData = securityResponse.data;
          setSecurityReqInput(securityData);

          progressHandler.updateMessage("Generating System Architecture");

          const archResponse = await brdToArchDesign({ brd: finalBRD });
          const archData = archResponse.data;
          setArchInput(archData);

          await progressHandler.finishAndClose();

          navigate("/dashboard/security-testing/requirement-output", {
            state: {
              funcMod: funcModules,
              securityReq: securityData,
              archDesign: archData,
              brd: finalBRD
            },
          });
        } catch (error) {
          setLoadingState({ open: false, message: "", progress: null });
          console.error("Error occurred:", error);
          console.error(
            "Session Expired",
            error.response?.data?.message === "Your session has expired."
          );
          if (error?.response?.data?.message === "Your session has expired.") {
            setOpenErrorPopup(true);
          }
        }
      }
    } catch (error) {
      console.error("Error during identification phase:", error);
      alert(error.message || "An unexpected error occurred.");
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setSecurityLoaderState(null);
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  return (
    <>
      <CommonAccordion
        summary={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Typography variant="subtitle1" sx={{ fontSize: "16px" }}>
              Requirement Phase
            </Typography>
          </Box>
        }
        details={
          <Box sx={{ pl: 1.2 }}>
            <Typography
              variant="body1"
              fontWeight="600"
              sx={{ mb: 1, fontSize: "16px" }}
            >
              Select Input Type
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, color: "text.secondary", width: "90%" }}
            >
              Please upload any one of the following inputs
            </Typography>
            <RadioGroup value={inputType} onChange={handleInputTypeChange}>
              <FormControlLabel
                value="BRD"
                control={<Radio />}
                label="Business Requirement Document (BRD)"
              />
              <FormControlLabel
                value="UserStories"
                control={<Radio />}
                label="User Stories"
              />
              <FormControlLabel
                value="Workflow"
                control={<Radio />}
                label="Workflow Documentation"
              />
              <FormControlLabel
                value="ADD"
                control={<Radio />}
                label="Architecture Design Document "
              />
            </RadioGroup>

            {inputType === "BRD" && (
              <Grid2 xs={12} spacing={2} sx={{ pr: 5, mb: 2 }}>
                <BRDComponent
                  fileRefMap={fileRefMap}
                  uploadedBRDFile={uploadedBRDFile}
                  handleFileUpload={handleFileUpload}
                  handleRemove={handleRemove}
                />
              </Grid2>
            )}

            {inputType === "UserStories" && (
              <Grid2 xs={12} spacing={2} sx={{ pr: 5, mb: 2 }}>
                <UserStoriesComponent
                  fileRef={fileRefMap.UserStories}
                  uploadedFile={uploadedUserStoriesFile}
                  handleFileUpload={(e) => handleFileUpload(e, "UserStories")}
                  handleRemove={() => handleRemove("UserStories")}
                  stories={stories}
                  setStories={setStories}
                />
              </Grid2>
            )}

            {inputType === "Workflow" && (
              <Grid2 xs={12} spacing={2} sx={{ pr: 5, mb: 2 }}>
                <WorkflowComponent
                  fileRef={fileRefMap.Workflow}
                  uploadedFile={uploadedWorkflowFile}
                  handleFileUpload={(e, key) => handleFileUpload(e, key)}
                  handleRemove={(key) => handleRemove(key)}
                  workflowInput={workflowInput}
                  setWorkflowInput={setWorkflowInput}
                />
              </Grid2>
            )}

            {inputType === "ADD" && (
              <Grid2 xs={12} spacing={2} sx={{ pr: 5, mb: 2 }}>
                <ADDComponent
                  fileRef={fileRefMap.Workflow.add}
                  uploadedFile={uploadedADDFile}
                  handleFileUpload={(e) => handleFileUpload(e, "add")}
                  handleRemove={() => handleRemove("add")}
                />
              </Grid2>
            )}
          </Box>
        }

        buttons={[
          {
            label: "Generate",
            variant: "contained",
            startIcon: <SendIcon />,
            sx: {
              float: "right",
              textTransform: "none",
              backgroundColor: "#49A3FF",
              color: "white",
              "&:hover": {
                backgroundColor: "#49A3FF",
              },
            },
            disabled: !isButtonEnabled(),
            onClick: handleIdentifyClick,
          },
        ]}
        type={"input"}
        defaultExpanded={true}
      />

      {openErrorPopup && <SessionExpired />}

    </>
  );
};

export default RequirementsPhase;
